import { getCLS, getFCP, getLCP, getTTFB, getFID } from 'web-vitals';
import { env } from './env';

const OTEL_ENABLED = env.VITE_OTEL_ENABLED === 'true';
const COLLECTOR_URL: string = env.VITE_OTEL_COLLECTOR_URL || '';
const CORS_PATTERN = /.*(parts-badger\.com|partsbadger\.com|ruggedvid\.com).*/;

async function initOtel(): Promise<void> {
	if (!OTEL_ENABLED || !COLLECTOR_URL) return;

	const { WebTracerProvider, BatchSpanProcessor } = await import(
		'@opentelemetry/sdk-trace-web'
	);
	const { OTLPTraceExporter } = await import(
		'@opentelemetry/exporter-trace-otlp-http'
	);
	const { DocumentLoadInstrumentation } = await import(
		'@opentelemetry/instrumentation-document-load'
	);
	const { FetchInstrumentation } = await import(
		'@opentelemetry/instrumentation-fetch'
	);
	const { XMLHttpRequestInstrumentation } = await import(
		'@opentelemetry/instrumentation-xml-http-request'
	);
	const { UserInteractionInstrumentation } = await import(
		'@opentelemetry/instrumentation-user-interaction'
	);
	const { LongTaskInstrumentation } = await import(
		'@opentelemetry/instrumentation-long-task'
	);
	const { ZoneContextManager } = await import('@opentelemetry/context-zone');
	const { Resource } = await import('@opentelemetry/resources');
	const {
		SEMRESATTRS_SERVICE_NAME,
		SEMRESATTRS_DEPLOYMENT_ENVIRONMENT,
		SEMRESATTRS_SERVICE_VERSION,
	} = await import('@opentelemetry/semantic-conventions');
	const { registerInstrumentations } = await import(
		'@opentelemetry/instrumentation'
	);
	const { trace } = await import('@opentelemetry/api');

	const resource = new Resource({
		[SEMRESATTRS_SERVICE_NAME]: 'fastlane-portal',
		[SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: env.VITE_ENVIRONMENT || 'development',
		[SEMRESATTRS_SERVICE_VERSION]: env.VITE_VERSION || 'unknown',
	});

	const exporter = new OTLPTraceExporter({
		url: `${COLLECTOR_URL}/v1/traces`,
	});

	const provider = new WebTracerProvider({ resource });
	provider.addSpanProcessor(new BatchSpanProcessor(exporter));
	provider.register({ contextManager: new ZoneContextManager() });

	registerInstrumentations({
		instrumentations: [
			new DocumentLoadInstrumentation(),
			new FetchInstrumentation({
				propagateTraceHeaderCorsUrls: [CORS_PATTERN],
			}),
			new XMLHttpRequestInstrumentation({
				propagateTraceHeaderCorsUrls: [CORS_PATTERN],
			}),
			new UserInteractionInstrumentation(),
			new LongTaskInstrumentation(),
		],
	});

	const tracer = trace.getTracer('fastlane-portal-vitals');
	function reportVital(metric: {
		name: string;
		value: number;
		id: string;
		delta: number;
	}) {
		const span = tracer.startSpan('web-vital', {
			attributes: {
				'web_vital.name': metric.name,
				'web_vital.value': metric.value,
				'web_vital.delta': metric.delta,
				'web_vital.id': metric.id,
			},
		});
		span.end();
	}

	getLCP(reportVital);
	getFCP(reportVital);
	getCLS(reportVital);
	getTTFB(reportVital);
	getFID(reportVital);

	console.info('[OTel] Browser tracing initialized, collector:', COLLECTOR_URL);
}

initOtel().catch((err) => {
	console.warn('[OTel] Failed to initialize browser tracing:', err);
});

export {};
